// services/frontend/src/types/upload.ts
// T005: TypeScript types for batch upload state management

/**
 * Message shown when uploads are blocked because another collection has active uploads
 * Feature 847: Used consistently across CollectionEditor and CollectionList
 */
export const UPLOADS_BLOCKED_MESSAGE = 'Another collection is uploading. Please wait for it to complete.';

/**
 * Upload states for individual files in the batch queue
 */
export type UploadState = 'pending' | 'uploading' | 'success' | 'error';

/**
 * Individual file upload item in the queue
 */
export interface FileUploadItem {
  /** Unique identifier for this upload item (UUID) */
  id: string;
  /** Browser File object containing the actual file data */
  file: File;
  /** Current state of this upload */
  state: UploadState;
  /** Upload progress percentage (0-100) */
  progress: number;
  /** Database ID of created photo (populated on success) */
  photoId: string | null;
  /** Error message if upload failed */
  error: string | null;
  /** Derived from file.name for display purposes */
  filename: string;
  /** File size in bytes (from file.size) */
  size: number;
  /** Timestamp when upload started */
  startedAt: Date | null;
  /** Timestamp when upload completed (success or failure) */
  completedAt: Date | null;
  /**
   * Position assigned when file is added to queue (not when upload completes).
   * This preserves the original file selection order regardless of which
   * uploads complete first. Only used for collection uploads.
   */
  intendedPosition: number | null;
}

/**
 * Overall state of a batch upload operation
 */
export interface BatchUploadState {
  /** All files in this batch */
  files: FileUploadItem[];
  /** Count of files currently uploading (state = 'uploading') */
  activeCount: number;
  /** Count of files waiting to upload (state = 'pending') */
  pendingCount: number;
  /** Count of successfully uploaded files (state = 'success') */
  completedCount: number;
  /** Count of failed uploads (state = 'error') */
  failedCount: number;
  /** Total number of files (files.length) */
  totalCount: number;
  /** Overall progress percentage (0-100) */
  overallProgress: number;
  /** True when all files are in 'success' or 'error' state */
  isComplete: boolean;
  /** True if any files are in 'error' state */
  hasFailures: boolean;
}

/**
 * Reducer actions for batch upload state management (discriminated union)
 */
export type UploadAction =
  | { type: 'ADD_FILES'; payload: { files: File[] } }
  | { type: 'ADD_ITEMS'; payload: { items: FileUploadItem[] } }
  | { type: 'START_UPLOAD'; payload: { id: string; startedAt: Date } }
  | { type: 'UPDATE_PROGRESS'; payload: { id: string; progress: number } }
  | { type: 'COMPLETE_UPLOAD'; payload: { id: string; photoId: string; completedAt: Date } }
  | { type: 'FAIL_UPLOAD'; payload: { id: string; error: string; completedAt: Date } }
  | { type: 'RETRY_UPLOAD'; payload: { id: string } }
  | { type: 'REMOVE_FILE'; payload: { id: string } };

/**
 * Return type for useBatchUpload hook
 */
export interface UseBatchUploadReturn {
  state: BatchUploadState;
  addFiles: (files: File[]) => void;
  retryUpload: (id: string) => void;
  removeFile: (id: string) => void;
  clearCompleted: () => void;
}

/**
 * Information about a completed batch upload
 * Feature 762: Used for order verification after uploads complete
 */
export interface CompletedBatchInfo {
  /** Successfully uploaded items sorted by intended position */
  successfulUploads: Array<{
    photoId: string;
    intendedPosition: number;
    filename: string;
  }>;
  /** Count of failed uploads in this batch */
  failedCount: number;
  /** Total items in this batch */
  totalCount: number;
}

/**
 * Optional configuration for useBatchUpload hook
 * Feature 762: Supports defensive order verification
 */
export interface UseBatchUploadOptions {
  /**
   * Callback to reorder photos after batch completes.
   * Called with ordered photoIds to ensure correct collection order.
   * Feature 762: Defensive order verification
   */
  reorderPhotos?: (photoIds: string[]) => Promise<void>;

  /**
   * Callback when batch upload completes (all files processed).
   * Receives info about the completed batch for logging/verification.
   */
  onBatchComplete?: (info: CompletedBatchInfo) => void;
}
